import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import { Helmet } from "react-helmet-async";
import Spinner from "../../components/Spinner";
import { format } from "date-fns";

const siteUrl = "https://missionmovement.nl";
const fallbackTitle = "Mission Movement Blog";
const fallbackDescription =
  "Lees inzichten van Mission Movement over fysieke voorbereiding, mentale weerbaarheid en gerichte training.";
const fallbackImage = `${siteUrl}/og-image.jpg`;

const stripHtml = (value = "") => value.replace(/<[^>]*>/g, " ").trim();

const truncate = (value, maxLength = 160) => {
  if (!value || value.length <= maxLength) return value;
  return `${value.slice(0, maxLength).trim()}...`;
};

const getBlogDescription = (blog) => {
  const directDescription =
    blog?.metaDescription ||
    blog?.description ||
    blog?.excerpt ||
    blog?.summary;

  if (directDescription) {
    return truncate(stripHtml(directDescription));
  }

  const firstParagraph = blog?.content?.paragraphs?.find(Boolean);

  return firstParagraph
    ? truncate(stripHtml(firstParagraph))
    : fallbackDescription;
};

const getAbsoluteUrl = (path, fallback = "") => {
  if (!path) return fallback;
  if (/^https?:\/\//i.test(path)) return path;
  return `${siteUrl}${path.startsWith("/") ? path : `/${path}`}`;
};

const getIsoDate = (value) => {
  if (!value) return null;

  const date =
    typeof value.toDate === "function"
      ? value.toDate()
      : value.seconds
        ? new Date(value.seconds * 1000)
        : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const SingleBlog = () => {
  const { slug } = useParams();
  const [blogs, setBlogs] = useState([]);
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const q = query(collection(db, "blogs"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        const blogList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setBlogs(blogList);

        const current = blogList.find((b) => b.slug === slug);
        setBlog(current || null);
      } catch (error) {
        console.error("Error loading blog:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  const getReadingTime = () => {
    const words = blog?.content?.paragraphs?.join(" ").split(" ").length || 0;
    return `${Math.ceil(words / 200)} min lezen`;
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator
        .share({
          title: blog.title,
          text: blog.summary,
          url,
        })
        .catch((err) => console.error("Share failed:", err));
    } else {
      navigator.clipboard.writeText(url);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    }
  };

  const currentIndex = blogs.findIndex((b) => b.slug === slug);
  const prevBlog = blogs[currentIndex + 1];
  const nextBlog = blogs[currentIndex - 1];

  if (loading || !blog) {
    return (
      <div className="flex justify-center items-center h-screen bg-mmPage">
        <Spinner color="border-mmAccent" />
      </div>
    );
  }

  const canonicalSlug = blog.slug || slug;
  const canonicalUrl = `${siteUrl}/blogs/${canonicalSlug}`;
  const title = blog.title || fallbackTitle;
  const description = getBlogDescription(blog);
  const imageUrl = getAbsoluteUrl(
    blog.thumbnail || blog.ogImage,
    fallbackImage,
  );
  const datePublished = getIsoDate(blog.createdAt || blog.datePublished);
  const dateModified = getIsoDate(
    blog.updatedAt || blog.modifiedAt || blog.dateModified,
  );
  const blogPostSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    image: imageUrl,
    url: canonicalUrl,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    author: {
      "@type": "Person",
      name: "Justin Peeters",
      url: `${siteUrl}/about`,
    },
    publisher: {
      "@type": "Organization",
      name: "Mission Movement",
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/img/mmvmtlogo.png`,
      },
    },
    inLanguage: "nl-NL",
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
  };

  return (
    <div className="min-h-screen pt-20 px-5 md:px-10 bg-mmPage text-mmText animate-fadein">
      <Helmet>
        <title>{title} | Mission Movement</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(blogPostSchema)}
        </script>
      </Helmet>

      <div className="max-w-3xl mx-auto">
        <Link
          to="/blogs"
          className="text-mmAccent text-sm font-semibold uppercase tracking-wide hover:underline mb-6 inline-block"
        >
          ← Terug
        </Link>

        <h1 className="mm-h1 text-mmText text-4xl md:text-6xl mb-4">{title}</h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-mmTextMuted mb-6">
          <span>Door {blog.author}</span>
          <span>•</span>
          <span>
            {blog.createdAt?.seconds &&
              format(new Date(blog.createdAt.seconds * 1000), "dd MMM yyyy")}
          </span>
          <span>•</span>
          <span>{getReadingTime()}</span>
          <button
            onClick={handleShare}
            className="ml-auto text-mmAccent text-xs border border-mmAccent px-3 py-1 rounded hover:bg-mmAccent hover:text-black transition"
          >
            Deel dit artikel
          </button>
        </div>

        {blog.tags?.length > 0 && (
          <div className="flex gap-2 flex-wrap mt-2 mb-4">
            {blog.tags.map((tag, i) => (
              <span
                key={i}
                className="bg-mmAccent text-white text-xs px-2 py-1 rounded-full font-semibold uppercase"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <img
          src={blog.thumbnail || fallbackImage}
          alt={title}
          className="w-full rounded-xl max-h-[500px] object-cover mb-10 shadow-md"
          width={1200}
          height={630}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />

        {blog.content?.quote && (
          <blockquote className="bg-mmSurface border-l-4 border-mmAccent p-6 italic text-lg mb-10 rounded-2xl text-mmTextMuted shadow-sm">
            “{blog.content.quote}”
          </blockquote>
        )}

        <div className="space-y-6 text-mmTextMuted leading-relaxed md:px-4 bg-mmSurface border border-mmBorder rounded-2xl p-5 shadow-sm">
          {blog.content?.paragraphs?.map((para, index) => (
            <div key={index} className=" text-sm md:text-base">
              <p>{para}</p>
            </div>
          ))}
        </div>

        {blog.content?.images?.length > 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            {blog.content.images.slice(1).map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`Visual ${i + 2}`}
                className="rounded-lg shadow-md"
                width={800}
                height={600}
                loading="lazy"
                decoding="async"
              />
            ))}
          </div>
        )}

        <div className="flex justify-between items-center mt-16">
          {prevBlog ? (
            <Link
              to={`/blogs/${prevBlog.slug}`}
              className="text-mmAccent text-sm hover:underline"
            >
              ← {prevBlog.title}
            </Link>
          ) : (
            <span />
          )}
          {nextBlog && (
            <Link
              to={`/blogs/${nextBlog.slug}`}
              className="text-mmAccent text-sm hover:underline ml-auto"
            >
              {nextBlog.title} →
            </Link>
          )}
        </div>

        <div className="mt-20 text-center">
          <h2 className="mm-h2 text-mmText mb-3">Vond je dit interessant?</h2>
          <p className="text-mmTextMuted mb-6 text-base md:text-lg">
            Ontdek meer training inzichten of bekijk onze programma’s die zijn
            ontworpen om jouw veerkracht naar een elitair niveau te tillen.
          </p>
          <Link
            to="/program"
            className="mb-16 inline-block mm-btnPrimary px-6 py-3"
          >
            <span>Bekijk trainingsprogramma</span>
            <span className="shrink-0">→</span>
          </Link>
        </div>

        {showToast && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-mmAccent text-white px-4 py-2 rounded shadow-sm text-sm font-bold z-50 animate-fadein">
            Link gekopieerd naar klembord!
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleBlog;
